package devxertech.com.pages.services.aiDevelopmentService

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import devxertech.com.section.DesktopNavigationAnchor
import devxertech.com.theme.SitePalette
import devxertech.com.utils.Constants
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private data class AiUseCase(val title: String, val description: String)

private val aiUseCases = listOf(
    AiUseCase(
        "Internal Knowledge Assistants",
        "Help employees find answers in approved business documents and knowledge sources, with access controls and source references where supported."
    ),
    AiUseCase(
        "Document Search & Assistance",
        "Help teams search documents, summarise information and prepare drafts, with human review for important decisions."
    ),
    AiUseCase(
        "Business Workflow Automation",
        "Connect repetitive steps across existing tools, such as collecting requests, organising information and preparing follow-up tasks. Integrations depend on platform access and technical feasibility."
    )
)

private val deliverySteps = listOf(
    "Define the problem and success criteria.",
    "Review available data, permissions and integrations.",
    "Build a focused prototype.",
    "Test accuracy, limitations and failure handling.",
    "Launch the agreed scope and review performance."
)

private val aiFaqs = listOf(
    "How is an internal AI assistant different from a chatbot?" to
        "An internal assistant helps employees find or work with approved business information and tools. A customer-facing chatbot focuses on conversations with customers, such as support questions or lead qualification. The right approach depends on who will use it and what task it should support.",
    "Can an assistant connect to our existing documents and tools?" to
        "That depends on the document formats, platforms, available APIs, account permissions and technical feasibility. We review those details before agreeing on an integration scope.",
    "How are data access and privacy considered?" to
        "We review which sources may be used, who is allowed to access them, and how the selected platforms handle data. Access controls and deployment choices depend on the systems available and the project requirements; these details should be agreed before implementation.",
    "What happens when an AI answer is incorrect or incomplete?" to
        "The system should be tested against representative questions and known limitations. Depending on the use case, it can be designed to show source references where supported, indicate uncertainty, ask for clarification or route work for human review. Important decisions should retain appropriate human oversight.",
    "What determines project cost and timeline?" to
        "The scope, data readiness, number and type of integrations, access requirements, testing needs and deployment environment all affect the work involved. We review these factors with you before defining a project estimate or schedule."
)

@Composable
fun AiServiceUseCases(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        id("ai-service-use-cases")
        attr("aria-labelledby", "ai-service-use-cases-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            property("padding", if (breakpoint <= Breakpoint.MD) "38px 20px" else "60px 40px")
            property("background-color", palette.softwareSectionBackground.hex)
            property("color", palette.commonSecondaryButtonColor.hex)
        }
    }) {
        Div(attrs = { style { property("max-width", "1120px"); property("margin", "0 auto") } }) {
            H2(attrs = {
                attr("id", "ai-service-use-cases-heading")
                style {
                    property("margin", "0 0 26px")
                    property("font-family", Constants.ROBOTO_FONT)
                    property("font-size", if (breakpoint <= Breakpoint.MD) "25px" else "34px")
                    property("font-weight", "700")
                    property("line-height", "1.25")
                    property("text-align", "center")
                }
            }) { Text("Practical AI assistant and automation use cases") }
            Div(attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", if (breakpoint <= Breakpoint.MD) "minmax(0, 1fr)" else "repeat(3, minmax(0, 1fr))")
                    property("gap", if (breakpoint <= Breakpoint.MD) "14px" else "22px")
                    property("align-items", "stretch")
                }
            }) {
                aiUseCases.forEach { useCase ->
                    Div(attrs = {
                        style {
                            property("box-sizing", "border-box")
                            property("min-width", "0")
                            property("height", "100%")
                            property("padding", if (breakpoint <= Breakpoint.MD) "20px" else "24px")
                            property("border", "1px solid rgba(37, 77, 121, 0.14)")
                            property("border-radius", "10px")
                            property("background-color", palette.bodyBackgroundSecondary.hex)
                            property("box-shadow", "0 8px 24px rgba(37, 77, 121, 0.06)")
                        }
                    }) {
                        H3(attrs = {
                            style {
                                property("margin", "0 0 12px")
                                property("font-family", Constants.ROBOTO_FONT)
                                property("font-size", if (breakpoint <= Breakpoint.MD) "19px" else "21px")
                                property("line-height", "1.35")
                            }
                        }) { Text(useCase.title) }
                        P(attrs = {
                            style {
                                property("margin", "0")
                                property("font-family", Constants.FONT_FAMILY)
                                property("font-size", "16px")
                                property("line-height", "1.7")
                            }
                        }) { Text(useCase.description) }
                    }
                }
            }
            P(attrs = {
                style {
                    property("margin", if (breakpoint <= Breakpoint.MD) "22px 0 0" else "28px 0 0")
                    property("font-family", Constants.FONT_FAMILY)
                    property("font-size", "16px")
                    property("line-height", "1.7")
                    property("text-align", "center")
                }
            }) {
                Text("For a practical starting point, read our ")
                AiPageLink("/blogs/choose-first-ai-automation-project", "guide to choosing your first AI automation project", palette)
                Text(". For customer conversations, support and lead qualification, see ")
                AiPageLink("/chatbot-development", "our chatbot development service", palette)
                Text(" or explore our ")
                AiPageLink("/solutions", "broader AI solutions", palette)
                Text(".")
            }
        }
    }
}

@Composable
fun AiServiceDeliveryProcess(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        id("ai-service-delivery-process")
        attr("aria-labelledby", "ai-service-delivery-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            property("padding", if (breakpoint <= Breakpoint.MD) "38px 20px" else "56px 40px")
            property("color", palette.commonSecondaryButtonColor.hex)
        }
    }) {
        Div(attrs = { style { property("max-width", "1120px"); property("margin", "0 auto") } }) {
            H2(attrs = {
                attr("id", "ai-service-delivery-heading")
                style {
                    property("margin", "0 0 24px")
                    property("font-family", Constants.ROBOTO_FONT)
                    property("font-size", if (breakpoint <= Breakpoint.MD) "25px" else "34px")
                    property("font-weight", "700")
                    property("line-height", "1.25")
                    property("text-align", "center")
                }
            }) { Text("A focused delivery process") }
            Div(attrs = {
                attr("role", "list")
                style {
                    property("display", "grid")
                    property("grid-template-columns", if (breakpoint <= Breakpoint.MD) "minmax(0, 1fr)" else "repeat(5, minmax(0, 1fr))")
                    property("gap", "12px")
                }
            }) {
                deliverySteps.forEachIndexed { index, step ->
                    Div(attrs = {
                        attr("role", "listitem")
                        style {
                            property("box-sizing", "border-box")
                            property("min-width", "0")
                            property("padding", if (breakpoint <= Breakpoint.MD) "16px" else "18px")
                            property("border-radius", "8px")
                            property("background-color", palette.softwareSectionBackground.hex)
                            property("line-height", "1.6")
                        }
                    }) {
                        H3(attrs = {
                            style {
                                property("margin", "0 0 8px")
                                property("font-family", Constants.ROBOTO_FONT)
                                property("font-size", "16px")
                                property("font-weight", "700")
                                property("color", palette.primary.hex)
                            }
                        }) { Text("Step ${index + 1}") }
                        Text(step)
                    }
                }
            }
        }
    }
}

@Composable
fun AiServiceFaq(breakpoint: Breakpoint, palette: SitePalette) {
    var openFaqIndex by remember { mutableStateOf<Int?>(null) }
    Section(attrs = {
        id("ai-service-faq")
        attr("aria-labelledby", "ai-service-faq-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            property("padding", if (breakpoint <= Breakpoint.MD) "38px 20px" else "56px 40px")
            property("background-color", palette.softwareSectionBackground.hex)
            property("color", palette.commonSecondaryButtonColor.hex)
        }
    }) {
        Div(attrs = { style { property("max-width", "900px"); property("margin", "0 auto") } }) {
            H2(attrs = {
                attr("id", "ai-service-faq-heading")
                style {
                    property("margin", "0 0 24px")
                    property("font-family", Constants.ROBOTO_FONT)
                    property("font-size", if (breakpoint <= Breakpoint.MD) "25px" else "34px")
                    property("font-weight", "700")
                    property("text-align", "center")
                }
            }) { Text("AI assistant and automation FAQs") }
            Div(attrs = { style { property("display", "grid"); property("gap", "12px") } }) {
                aiFaqs.forEachIndexed { index, (question, answer) ->
                    val questionId = "ai-service-faq-question-$index"
                    val answerId = "ai-service-faq-answer-$index"
                    val isOpen = openFaqIndex == index
                    Div(attrs = {
                        style {
                            property("box-sizing", "border-box")
                            property("padding", if (breakpoint <= Breakpoint.MD) "14px" else "18px")
                            property("border", "1px solid rgba(37, 77, 121, 0.14)")
                            property("border-radius", "8px")
                            property("background-color", palette.bodyBackgroundSecondary.hex)
                        }
                    }) {
                        H3(attrs = {
                            style { property("margin", "0") }
                        }) {
                            Button(attrs = {
                                attr("id", questionId)
                                attr("type", "button")
                                attr("aria-expanded", isOpen.toString())
                                attr("aria-controls", answerId)
                                onClick { openFaqIndex = if (isOpen) null else index }
                                style {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "space-between")
                                    property("gap", "16px")
                                    property("width", "100%")
                                    property("padding", "0")
                                    property("border", "0")
                                    property("background", "transparent")
                                    property("color", palette.commonSecondaryButtonColor.hex)
                                    property("font-family", Constants.ROBOTO_FONT)
                                    property("font-size", if (breakpoint <= Breakpoint.MD) "18px" else "20px")
                                    property("font-weight", "700")
                                    property("line-height", "1.4")
                                    property("text-align", "left")
                                    property("cursor", "pointer")
                                }
                            }) {
                                Span(attrs = {
                                    style {
                                        property("min-width", "0")
                                        property("flex", "1")
                                        property("text-align", "left")
                                    }
                                }) { Text(question) }
                                Span(attrs = { attr("aria-hidden", "true") }) {
                                    Text(if (isOpen) "−" else "+")
                                }
                            }
                        }
                        Div(attrs = {
                            id(answerId)
                            attr("role", "region")
                            attr("aria-labelledby", questionId)
                            if (!isOpen) attr("hidden", "")
                            style { property("padding-top", if (isOpen) "12px" else "0") }
                        }) {
                            P(attrs = {
                                style {
                                    property("margin", "0")
                                    property("font-family", Constants.FONT_FAMILY)
                                    property("font-size", "16px")
                                    property("line-height", "1.7")
                                }
                            }) { Text(answer) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AiServiceContactCta(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        attr("aria-labelledby", "ai-service-contact-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            property("padding", if (breakpoint <= Breakpoint.MD) "38px 20px" else "48px 40px")
            property("text-align", "center")
        }
    }) {
        H2(attrs = {
            attr("id", "ai-service-contact-heading")
            style {
                property("margin", "0 0 18px")
                property("font-family", Constants.ROBOTO_FONT)
                property("font-size", if (breakpoint <= Breakpoint.MD) "25px" else "32px")
                property("line-height", "1.25")
            }
        }) { Text("Have a clearly defined AI project in mind?") }
        DesktopNavigationAnchor(
            "/contact-us",
            Modifier
                .backgroundColor(palette.primary.rgb)
                .color(palette.bodyBackgroundSecondary.rgb)
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("min-height", "46px")
                    property("box-sizing", "border-box")
                    property("padding", "12px 22px")
                    property("border-radius", "6px")
                    property("font-family", Constants.ROBOTO_FONT)
                    property("font-size", "16px")
                    property("font-weight", "700")
                    property("text-decoration", "none")
                }
        ) { Text("Discuss Your AI Project") }
    }
}

@Composable
private fun AiPageLink(path: String, label: String, palette: SitePalette) {
    DesktopNavigationAnchor(
        path,
        Modifier.color(palette.primary.rgb).styleModifier {
            property("font-weight", "700")
            property("text-decoration", "underline")
            property("text-underline-offset", "3px")
        }
    ) { Text(label) }
}
